import { Haptics, ImpactStyle } from '@capacitor/haptics'
import {
  Box,
  Divider,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  Portal,
  MenuItem,
  MenuList,
} from '@chakra-ui/react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  ErrorPrompt,
  ListUneven,
  MessagesActiveFilled,
  MessagesFilled,
  NotificationFilled,
  NotificationsActiveFilled,
  Settings,
  Shop,
} from '../icons/icons'

import { GoQuestion } from 'react-icons/go'
import { PiRadio } from 'react-icons/pi'
import { HiOutlineCurrencyDollar } from 'react-icons/hi2'

import { useUserWrapperContext } from '../wrappers/UserWrapper'
import { SafeArea } from 'capacitor-plugin-safe-area'
import { useState, useEffect } from 'react'

const UserProfileHeader = ({
  user,
  isHost,
  setIsHost,
  onReportErrorModalOpen,
  logout,
  newNotifications,
  newMessages,
}: {
  user: any
  isHost: boolean
  setIsHost: (value: boolean) => void
  onReportErrorModalOpen: () => void
  logout: () => void
  newNotifications: number
  newMessages?: number
}) => {
  const { topCollapsed } = useUserWrapperContext()

  const triggerHapticFeedback = async (style = ImpactStyle.Heavy) => {
    Haptics.impact({ style }).catch(() => {})
  }

  const [searchParams] = useSearchParams()
  const id = searchParams.get('id')
  const isExternal = id !== null && id != user?.member_profile?.id

  const transition = '0.2s ease-out'
  const navigate = useNavigate()

  const [statusBarHeight, setStatusBarHeight] = useState<number>(0)

  useEffect(() => {
    SafeArea.getStatusBarHeight().then(({ statusBarHeight }) => {
      setStatusBarHeight(statusBarHeight)
    })
  }, [])

  return (
    <Box
      w="100%"
      h={topCollapsed ? '0' : 'auto'}
      overflow="hidden"
      bg={'white'}
      position="fixed"
      left="0"
      top="0"
      zIndex={1000}
      pt={`${statusBarHeight + 20}px`}
      transition={transition}
    >
      <Flex w="100%" justifyContent="space-between" alignItems="center" px="20px" pb="8px">
        <IconButton
          minW="32px"
          h="32px"
          variant="light"
          aria-label="back"
          onClick={() => {
            navigate(-1)
            triggerHapticFeedback(ImpactStyle.Medium)
          }}
          icon={
            <Icon width="20px" height="19px" viewBox="0 0 23 20">
              <ArrowLeft color="#171C22" />
            </Icon>
          }
        />

        {!isExternal && (
          <Flex gap="10px">
            <IconButton
              onClick={() => {
                triggerHapticFeedback(ImpactStyle.Medium)
                navigate('/user/notifications')
              }}
              size="sm"
              variant="light"
              aria-label="notifications"
              icon={
                <Icon w="22px" h="22px">
                  {newNotifications != 0 ? (
                    <NotificationsActiveFilled notificationCount={newNotifications} />
                  ) : (
                    <NotificationFilled />
                  )}
                </Icon>
              }
              borderRadius="full" // Makes the button circular
              _focus={{ boxShadow: 'none' }} // Removes the focus outline
              _active={{ bg: '#c4c4c4' }} // Removes the background on active state
              _hover={{ bg: 'transparent' }} // Removes the background on hover state
              draggable="false" // Prevents the button from being draggable
            />
            <Menu autoSelect={false}>
              <MenuButton
                as={IconButton}
                onClick={() => triggerHapticFeedback(ImpactStyle.Medium)}
                minW="32px"
                h="32px"
                variant="light"
                aria-label="expand"
                icon={
                  <Icon width="20px" height="20px" viewBox="0 0 20 20" color="#171C22">
                    <ListUneven />
                  </Icon>
                }
                borderRadius="full" // Ensures the button is circular
                _focus={{ boxShadow: 'none' }} // Removes focus outline
                _active={{ bg: '#c4c4c4' }} // Removes background on active state
                draggable="false" // Prevents the button from being draggable
              ></MenuButton>
              <Portal>
                <MenuList
                  borderRadius="10px"
                  boxShadow="0px 12.914606094360352px 86.71235656738281px 0px #2B2C2D14"
                  zIndex={10001}
                  // position="absolute"
                >
                  <MenuItem
                    onClick={() => {
                      navigate('/user/settings/index')
                      triggerHapticFeedback(ImpactStyle.Medium)
                    }}
                    fontSize="md"
                    fontWeight="500"
                    color="#171C22"
                  >
                    <Icon mr="8px" w="24px" h="24px" fill="none" viewBox="0 0 24 24">
                      <Settings />
                    </Icon>
                    <span>Settings</span>
                  </MenuItem>
                  <MenuItem
                    fontSize="md"
                    fontWeight="500"
                    color="#171C22"
                    onClick={() => {
                      onReportErrorModalOpen()
                      triggerHapticFeedback(ImpactStyle.Medium)
                    }}
                  >
                    <Icon mr="8px" w="24px" h="24px" fill="none" viewBox="0 0 24 24">
                      <ErrorPrompt />
                    </Icon>
                    <span>Report a problem</span>
                  </MenuItem>
                  <MenuItem
                    fontSize="md"
                    fontWeight="500"
                    color="#171C22"
                    onClick={() => navigate('/user/messages?to=f6f1a782-2afc-48ba-8683-3bab1d8cec07')}
                  >
                    <Icon mr="8px" w="24px" h="24px" fill="none" viewBox="0 0 16 16">
                      <GoQuestion />
                    </Icon>
                    <span>Contact LVBL</span>
                  </MenuItem>
                  <MenuItem
                    fontSize="md"
                    fontWeight="500"
                    color="#171C22"
                    onClick={() => navigate('/user/settings/wallet')}
                  >
                    <Icon mr="8px" w="24px" h="24px" fill="none" viewBox="0 0 15 15">
                      <HiOutlineCurrencyDollar />
                    </Icon>
                    <span>My Wallet</span>
                  </MenuItem>
                  <MenuItem
                    fontSize="md"
                    fontWeight="500"
                    color="#171C22"
                    onClick={() => navigate('/user/messages?to=-2afc-48ba-8683-3bab1d8cec07')}
                  >
                    <Icon mr="8px" w="24px" h="24px" fill="none" viewBox="0 0 16 16">
                      <PiRadio />
                    </Icon>
                    <span>LVBL Radio</span>
                  </MenuItem>
                  <MenuItem fontSize="md" fontWeight="500" color="#171C22" onClick={() => navigate('/user/shop')}>
                    <Icon mr="8px" w="24px" h="24px" fill="none" viewBox="0 0 28 28">
                      <Shop />
                    </Icon>
                    <span>ProShop</span>
                  </MenuItem>
                  <MenuDivider />
                  {user?.member_profile?.is_coach && (
                    <MenuItem
                      fontSize="md"
                      fontWeight="500"
                      color="#171C22"
                      onClick={() => {
                        triggerHapticFeedback(ImpactStyle.Medium)
                        setIsHost(!isHost)
                      }}
                    >
                      <span>Switch to {isHost ? 'member' : 'host'} account</span>
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      triggerHapticFeedback(ImpactStyle.Medium)
                      logout()
                    }}
                    fontSize="md"
                    fontWeight="500"
                    color="#FF3B30"
                  >
                    <span>Log out</span>
                  </MenuItem>
                </MenuList>
              </Portal>
            </Menu>
          </Flex>
        )}
        {isExternal && (
          <Flex gap="10px">
            <IconButton
              onClick={() => {
                triggerHapticFeedback(ImpactStyle.Medium)
                navigate('/user/notifications')
              }}
              size="sm"
              variant="light"
              aria-label="notifications"
              icon={
                <Icon w="22px" h="22px">
                  {newNotifications != 0 ? (
                    <NotificationsActiveFilled notificationCount={newNotifications} />
                  ) : (
                    <NotificationFilled />
                  )}
                </Icon>
              }
              borderRadius="full" // Makes the button circular
              _focus={{ boxShadow: 'none' }} // Removes the focus outline
              _active={{ bg: '#c4c4c4' }} // Removes the background on active state
              _hover={{ bg: 'transparent' }} // Removes the background on hover state
              draggable="false" // Prevents the button from being draggable
            />
            <IconButton
              onClick={() => {
                triggerHapticFeedback(ImpactStyle.Medium)
                navigate('/user/messages')
              }}
              size="sm"
              variant="light"
              aria-label="messages"
              icon={
                <Icon w="22px" h="22px">
                  {newMessages ? <MessagesActiveFilled messagesCount={newMessages} /> : <MessagesFilled />}
                </Icon>
              }
              borderRadius="full" // Makes the button circular
              _focus={{ boxShadow: 'none' }} // Removes the focus outline
              _active={{ bg: '#c4c4c4' }} // Removes the background on active state
              _hover={{ bg: 'transparent' }} // Removes the background on hover state
              draggable="false" // Prevents the button from being draggable
            />
          </Flex>
        )}
      </Flex>
      <Divider />
    </Box>
  )
}

export default UserProfileHeader
